import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Navigate, Link } from 'react-router-dom';
import { Search, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const API = `${window.location.origin}/api`;

/**
 * Dedicated landing page per category.
 * Route: /shop/:slug  (e.g. /shop/perfume, /shop/helmet-spray, /shop/car-spray)
 *
 * - Pulls the matching category template (label, tagline, icon, match_categories).
 * - Filters products to those whose `category` field is one of template.match_categories.
 * - Per-category SEO + breadcrumbs.
 * - If slug is unknown → renders the generic 'all' grid (no redirect).
 */
export default function CategoryShopPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const activeGender = searchParams.get('gender') || 'All';

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/category-template/${slug}`).catch(() => ({ data: null })),
    ]).then(([prodRes, tplRes]) => {
      setProducts(prodRes.data || []);
      setTemplate(tplRes.data);
    }).finally(() => setLoading(false));
  }, [slug]);

  // Filter products by template.match_categories
  const filtered = useMemo(() => {
    let list = products;
    if (template?.match_categories?.length) {
      const matchSet = new Set(template.match_categories.map(c => c.toLowerCase().trim()));
      list = list.filter(p => matchSet.has((p.category || '').toLowerCase().trim()));
    }
    if (activeGender !== 'All') {
      list = list.filter(p => p.gender === activeGender);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, template, activeGender, search]);

  // Hide category from public if it has no products yet (empty catalogs shouldn't be browsable).
  // We compute this BEFORE search/gender filters so a search miss doesn't trigger a redirect.
  const totalInCategory = useMemo(() => {
    if (!template?.match_categories?.length) return products.length;
    const matchSet = new Set(template.match_categories.map(c => c.toLowerCase().trim()));
    return products.filter(p => matchSet.has((p.category || '').toLowerCase().trim())).length;
  }, [products, template]);

  if (!loading && !template) {
    return <Navigate to="/shop" replace />;
  }
  if (!loading && template && totalInCategory === 0) {
    return <Navigate to="/shop" replace />;
  }

  const showGenderFilter = template?.detail_layout === 'perfume' || template?.detail_layout === 'body_mist';
  const seoTitle = template ? `Shop ${template.label}` : 'Shop';
  const seoDesc = template?.tagline || `Browse ${template?.label || 'all'} from JS Fragrance.`;
  const canonicalUrl = `https://jsfragrances.com/shop/${slug}`;

  return (
    <div data-testid={`category-shop-${slug}`} className="min-h-screen">
      <SEOHead title={seoTitle} description={seoDesc} url={canonicalUrl} />
      <BreadcrumbSchema crumbs={[
        { name: 'Home', path: '/' },
        { name: 'Shop', path: '/shop' },
        { name: template?.label || slug, path: `/shop/${slug}` },
      ]} />

      {/* HERO */}
      <section className="relative border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1C7D3F]/15 via-transparent to-[#D4AF37]/8 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-12 text-center">
          <Link to="/shop" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-white/40 hover:text-white/80 mb-5 font-sans" data-testid="back-to-shop">
            <ArrowLeft className="w-3 h-3" /> All categories
          </Link>
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 font-sans">JS Fragrance · {template?.label}</p>
          <h1 className="text-4xl sm:text-5xl text-white font-light leading-tight mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {template?.label}
          </h1>
          {template?.tagline && (
            <p className="text-white/55 text-sm sm:text-base font-sans max-w-xl mx-auto">{template.tagline}</p>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          {showGenderFilter ? (
            <div className="flex gap-2 flex-wrap" data-testid="gender-filters">
              {['All', 'Men', 'Women', 'Unisex'].map(g => (
                <button
                  key={g}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    if (g === 'All') next.delete('gender'); else next.set('gender', g);
                    setSearchParams(next);
                  }}
                  data-testid={`filter-${g.toLowerCase()}`}
                  className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-sans transition-colors ${
                    activeGender === g ? 'bg-[#D4AF37] text-black font-medium' : 'border border-white/15 text-white/60 hover:border-white/40'
                  }`}
                >{g}</button>
              ))}
            </div>
          ) : <div />}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full sm:w-64 pl-10 pr-4 py-2 bg-transparent border border-white/15 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
              data-testid="category-search-input"
            />
          </div>
        </div>

        <p className="text-white/40 text-sm mb-6 font-sans">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</p>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-[#141414] aspect-[3/4] mb-3" />
                <div className="h-3 bg-[#141414] w-2/3 mb-2" />
                <div className="h-3 bg-[#141414] w-1/3" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <p className="text-white/40 text-base mb-3 font-sans">No products in this category yet.</p>
            <Link to="/shop" className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-white border-b border-[#D4AF37]/40 pb-1 font-sans">
              Browse all fragrances
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 stagger-children" data-testid="products-grid">
            {filtered.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
